import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

// Signal Fire (AQ-003 + AQ-026) — ONE unread-activity refresher for the whole
// customer shell. It was born inside the Messages dropdown; every other surface
// that needs the same truth (the rail badge, the inbox, the order Chat tab) now
// reads it from here instead of arming a second timer. Mounted once in AppShell,
// which persists across Inertia visits, so the poll cost is unchanged no matter
// how many surfaces subscribe.
//
// It NEVER writes. Nothing here marks a conversation read or advances a
// participant's last_read_message_index — the read paths stay exactly where
// AQ-002 put them (opening a thread, opening the Chat tab, mark-all-read).

const MESSAGE_REFRESH_MS = 15_000

// A consumer outside the provider (a screen with no AppShell, e.g. VideoCall)
// degrades to "no live data" rather than throwing.
const NO_ACTIVITY = Object.freeze({
  threads: null,
  unread: 0,
  stamp: null,
  // refresh always answers with a promise, provider or not, so a caller can
  // sequence work on the response without a null check.
  refresh: () => Promise.resolve(),
  clearAllLocally: () => {},
})

const ShellActivityContext = createContext(NO_ACTIVITY)

export function useShellActivity() {
  return useContext(ShellActivityContext)
}

export function ShellActivityProvider({ unreadCount = 0, children }) {
  const [threads, setThreads] = useState(null) // null = not fetched yet
  const [unread, setUnread] = useState(unreadCount)
  // Server activity token (CustomerMessagesPresenter#stamp). null until the first
  // fetch lands; subscribers compare it, never render it.
  const [stamp, setStamp] = useState(null)
  const requestRef = useRef(null)

  // Follow the shared prop across Inertia visits (a new page carries a fresh
  // count), unless a surface has locally cleared it via "mark all read".
  useEffect(() => {
    setUnread(unreadCount)
  }, [unreadCount])

  // One request at a time. The same refresh powers the visible-page poll,
  // focus/visibility recovery and panel opening, so those triggers can never
  // fan out duplicate queries. Aborting on unmount prevents a stale response
  // from mutating a shell that has already gone away.
  //
  // `showLoading` means the caller is displaying a loading placeholder: a failed
  // fetch must then settle it to an empty list rather than leave it up forever.
  //
  // `force` is what a caller uses to reconcile AFTER its own write. Joining an
  // in-flight request would be wrong there: that request may have been issued
  // before the write committed, so its answer describes the old world and would
  // resurrect exactly what the write cleared. Forcing aborts whatever is in
  // flight and opens a fresh GET, which — being issued after the write settled —
  // necessarily reads post-write state. Everyone else keeps the shared request.
  const refresh = useCallback(({ showLoading = false, force = false } = {}) => {
    if (force) {
      requestRef.current?.controller.abort()
      requestRef.current = null
    } else if (requestRef.current) {
      return requestRef.current.promise
    }

    const controller = new AbortController()

    const promise = fetch("/menu/threads", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to refresh messages")
        return res.json()
      })
      .then((data) => {
        if (controller.signal.aborted) return
        setThreads(data.threads ?? [])
        setUnread(Number(data.unread) || 0)
        setStamp(Number(data.stamp) || 0)
      })
      .catch((error) => {
        if (error?.name !== "AbortError" && showLoading) {
          setThreads((current) => current ?? [])
        }
      })
      .finally(() => {
        if (requestRef.current?.promise === promise) requestRef.current = null
      })

    requestRef.current = { controller, promise }
    return promise
  }, [])

  // Server state is authoritative. Poll only while the document is visible,
  // refresh immediately when the customer returns, and remove every listener /
  // timer when AppShell unmounts (AQ-003).
  useEffect(() => {
    const refreshIfVisible = () => {
      if (!document.hidden) refresh()
    }

    refreshIfVisible()
    const interval = window.setInterval(refreshIfVisible, MESSAGE_REFRESH_MS)
    window.addEventListener("focus", refreshIfVisible)
    document.addEventListener("visibilitychange", refreshIfVisible)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener("focus", refreshIfVisible)
      document.removeEventListener("visibilitychange", refreshIfVisible)
      requestRef.current?.controller.abort()
      requestRef.current = null
    }
  }, [refresh])

  // The optimistic half of "mark all read": drop the local counts immediately and
  // cancel any in-flight poll, so a response captured BEFORE the write can't
  // restore what it just cleared. The caller owns the server write and calls
  // refresh({ force: true }) once it settles to reconcile (a failed write comes
  // back). The force matters: between this abort and the write settling, a poll
  // tick can open its own pre-write GET, and a plain refresh() would adopt it.
  const clearAllLocally = useCallback(() => {
    requestRef.current?.controller.abort()
    requestRef.current = null
    setUnread(0)
    setThreads((prev) => (prev ? prev.map((thread) => ({ ...thread, unread: 0 })) : prev))
  }, [])

  const value = useMemo(
    () => ({ threads, unread, stamp, refresh, clearAllLocally }),
    [threads, unread, stamp, refresh, clearAllLocally]
  )

  return <ShellActivityContext.Provider value={value}>{children}</ShellActivityContext.Provider>
}
